import { safeValidateUIMessages, type UIMessage } from "ai";
import { z } from "zod";
import { safeCopilotHref } from "@/features/copilot/lib/copilot-markdown-policy";

const appRelativeResultHrefSchema = z
  .string()
  .startsWith("/")
  .refine((value) => !value.startsWith("//"))
  .refine((value) => !value.includes("\\"))
  .refine(
    (value) =>
      ![...value].some((character) => {
        const code = character.codePointAt(0);
        return code !== undefined && (code <= 0x1f || code === 0x7f);
      }),
  )
  .refine((value) => safeCopilotHref(value, "https://copilot.invalid") !== null);

const copilotActionSummarySchema = z.strictObject({
  title: z.string().min(1).max(500),
  targets: z
    .array(
      z.strictObject({
        kind: z.string().min(1).max(80),
        id: z.string().min(1).max(200),
        label: z.string().min(1).max(500),
      }),
    )
    .max(32),
  consequence: z.string().min(1).max(1_000),
  /// Short plain sentences the server writes about what the action will do. Absent on actions
  /// stored before the field existed.
  facts: z.array(z.string().min(1).max(160)).min(1).max(8).optional(),
  undoCapability: z.string().min(1).max(120).nullable(),
});

export const copilotActionViewSchema = z.strictObject({
  toolCallId: z.string().min(1).max(200),
  capability: z.string().min(1).max(120),
  risk: z.enum(["safe_write", "decision"]),
  status: z.enum(["pending", "running", "confirmed", "declined", "expired", "failed", "unknown"]),
  reconciling: z.boolean(),
  args: z.record(z.string(), z.json()),
  summary: copilotActionSummarySchema,
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  result: z
    .strictObject({
      text: z.string().min(1),
      // Old stored rows carry an href; it is accepted but no longer drawn.
      href: appRelativeResultHrefSchema.optional(),
      recordId: z.string().min(1).max(128).optional(),
    })
    .nullable(),
  error: z
    .strictObject({
      code: z.string().min(1).max(120),
      message: z.string().min(1).max(500),
    })
    .nullable(),
});

export const copilotActionDataPartSchema = z.strictObject({
  actions: z.array(copilotActionViewSchema).max(20),
});

export type CopilotActionView = z.infer<typeof copilotActionViewSchema>;

const copilotTurnSchema = z.strictObject({
  id: z.string().min(1),
  status: z.enum(["running", "waiting_approval", "completed", "interrupted", "failed"]),
  modelCalls: z.number().int().nonnegative(),
  activeMs: z.number().int().nonnegative(),
  stoppedReason: z.string().nullable(),
  settlementRevision: z.number().int().nonnegative(),
});

const copilotConversationShape = z.strictObject({
  id: z.string().min(1),
  workspaceId: z.string().min(1),
  title: z.string(),
  createdAt: z.string().datetime(),
  lastActivityAt: z.string().datetime(),
  messages: z.array(z.unknown()),
  actions: z.array(copilotActionViewSchema).max(320).optional(),
  olderContextOmitted: z.boolean(),
  activeTurn: copilotTurnSchema.nullable(),
});

export type CopilotConversation = Omit<
  z.infer<typeof copilotConversationShape>,
  "messages" | "actions"
> & {
  messages: UIMessage[];
  actions: CopilotActionView[];
};

export async function parseCopilotConversation(input: unknown): Promise<CopilotConversation> {
  const parsed = copilotConversationShape.parse(input);
  if (parsed.messages.length === 0)
    return { ...parsed, actions: parsed.actions ?? [], messages: [] };
  const validated = await safeValidateUIMessages({ messages: parsed.messages });
  if (
    !validated.success ||
    validated.data.some((message) => message.role !== "user" && message.role !== "assistant")
  ) {
    throw new Error("The copilot conversation could not be verified.");
  }
  for (const message of validated.data) {
    for (const part of message.parts) {
      if (part.type !== "data-copilot-actions") continue;
      const result = copilotActionDataPartSchema.safeParse(part.data);
      if (!result.success) throw new Error("The copilot action data could not be verified.");
    }
  }
  return { ...parsed, actions: parsed.actions ?? [], messages: validated.data };
}

export const copilotConversationResponseSchema = z.union([copilotConversationShape, z.null()]);
export const copilotCreateResponseSchema = copilotConversationShape;
export const copilotStopResponseSchema = z.strictObject({
  status: z.enum(["idle", "stopping"]),
});

export type CopilotStopResponse = z.infer<typeof copilotStopResponseSchema>;
